// TableauOutputsPanel — Stage 4 sub-panel revealed when the user has
// the TDSX layer active. Surfaces the four Phase 7 sub-steps:
//
//   1. .tdsx download  (always, locked-on)
//   2. .twbx workbook download
//   3. Publish to Tableau Cloud
//   4. Pulse Metrics (subscribes the SE)
//
// Steps 3 & 4 require valid creds; the embedded TableauPatPicker
// gates them behind a saved profile or one-off PAT. The panel
// auto-disables Pulse if Publish is off (Pulse needs the
// datasource_id from the publish step) and re-enables it when
// Publish flips back on, mirroring the backend's _run_phase7
// constraint chain.

import type {
  TableauOutputsConfig,
  TableauPatOneOff,
} from "@/lib/types";
import { TableauPatPicker } from "./TableauPatPicker";

interface TableauOutputsPanelProps {
  value: TableauOutputsConfig;
  onChange: (next: TableauOutputsConfig) => void;
}

export function TableauOutputsPanel({
  value,
  onChange,
}: TableauOutputsPanelProps): JSX.Element {
  const setStep = (
    k: "tdsx" | "twbx" | "publish" | "pulse",
    on: boolean,
  ): void => {
    let next: TableauOutputsConfig = { ...value, [k]: on };
    // Pulse requires Publish — disabling Publish disables Pulse too.
    if (k === "publish" && !on) next = { ...next, pulse: false };
    // Re-enabling Pulse without Publish on auto-enables Publish.
    if (k === "pulse" && on && !value.publish) next = { ...next, publish: true };
    onChange(next);
  };

  const credsResolved = !!value.patProfileId || !!value.patOneOff;
  const credsRequired = value.publish || value.pulse;
  const credsMissing = credsRequired && !credsResolved;

  return (
    <div
      style={{
        marginTop: 18,
        padding: 16,
        borderRadius: 12,
        border: "1px solid #d2bcf3",
        background: "linear-gradient(135deg,#f6eefd, white 60%)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: "#ede0fb",
            color: "var(--violet)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: 800,
          }}
        >
          ✦
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
            Tableau outputs
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-2)",
              marginTop: 2,
              lineHeight: 1.45,
            }}
          >
            Pick which Tableau artefacts the pipeline produces from
            the generated data. The .tdsx is always built — TWBX and
            the Cloud round-trips are opt-in.
          </div>
        </div>
        <span
          className="chip ghost"
          style={{ fontSize: 10, alignSelf: "flex-start" }}
        >
          Phase 7
        </span>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <StepRow
          label="Build .tdsx (Hyper extract)"
          desc="Tableau Packaged Datasource — the always-on baseline output."
          on={value.tdsx}
          locked
          onToggle={() => undefined}
        />
        <StepRow
          label="Build .twbx (Packaged Workbook)"
          desc="Canonical Q Branch dashboards (Pipeline / Activity / Service / Account 360) wrapped with the .tdsx."
          on={value.twbx}
          onToggle={() => setStep("twbx", !value.twbx)}
        />
        <StepRow
          label="Publish to Tableau Cloud"
          desc={`Uploads the .tdsx to Generator/<your-email>/<pack> on the connected site.${credsMissing ? " Requires a PAT." : ""}`}
          on={value.publish}
          onToggle={() => setStep("publish", !value.publish)}
          warn={credsMissing && value.publish}
        />
        <StepRow
          label="Pulse Metrics (subscribe)"
          desc="Authors per-pack Pulse metrics + subscribes you. Requires Publish."
          on={value.pulse}
          onToggle={() => setStep("pulse", !value.pulse)}
          disabled={!value.publish && !value.pulse}
          warn={credsMissing && value.pulse}
        />
      </div>

      {/* Cred picker only visible when Publish or Pulse is on */}
      {credsRequired && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: "white",
            borderRadius: 10,
            border: "1px solid var(--bd)",
          }}
        >
          <TableauPatPicker
            selectedProfileId={value.patProfileId}
            oneOff={value.patOneOff}
            onSelectProfile={(profileId) =>
              onChange({ ...value, patProfileId: profileId })
            }
            onSetOneOff={(oneOff: TableauPatOneOff | null) =>
              onChange({ ...value, patOneOff: oneOff, patProfileId: null })
            }
          />
          {credsMissing && (
            <div
              role="alert"
              style={{
                marginTop: 10,
                fontSize: 11,
                color: "#b71c1c",
                background: "rgba(239,83,80,.08)",
                border: "1px solid rgba(239,83,80,.3)",
                padding: "6px 10px",
                borderRadius: 6,
              }}
            >
              ⚠ Add a Tableau Cloud PAT to enable Publish / Pulse, or
              clear those steps to launch with TDSX-only output.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StepRow({
  label,
  desc,
  on,
  locked,
  disabled,
  warn,
  onToggle,
}: {
  label: string;
  desc: string;
  on: boolean;
  locked?: boolean;
  disabled?: boolean;
  warn?: boolean;
  onToggle: () => void;
}): JSX.Element {
  const interactive = !locked && !disabled;
  return (
    <button
      type="button"
      onClick={interactive ? onToggle : undefined}
      disabled={!interactive}
      style={{
        textAlign: "left",
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${
          warn ? "rgba(239,83,80,.5)" : on ? "var(--violet)" : "var(--bd)"
        }`,
        background: on ? "white" : "rgba(255,255,255,.7)",
        opacity: disabled ? 0.5 : 1,
        cursor: interactive ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          flexShrink: 0,
          background: on ? "var(--violet)" : "white",
          border: `1.5px solid ${on ? "var(--violet)" : "var(--bd)"}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: 11,
          fontWeight: 800,
        }}
      >
        {on ? "✓" : ""}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--ink)",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>{label}</span>
          {locked && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 999,
                background: "rgba(0,118,211,.12)",
                color: "var(--eb-30)",
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              Always on
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-2)", marginTop: 2 }}>
          {desc}
        </div>
      </div>
    </button>
  );
}
